import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight,
  ArrowLeft,
  MapPin,
  Phone,
  Maximize,
  Bed,
  Bath,
  Eye,
  Loader,
  Home,
  Users,
  UserPlus,
  UserCheck,
  ArrowUpDown,
  Building2,
  FileText
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import networkService from '../services/networkService';
import GooglePagination from '../components/GooglePagination';
import { useAuth } from '../context/AuthContext';
import { formatPrice, formatArea, getPropertyTypeColor, getStatusColor } from '../utils/helpers';
import './OffersPage.css';
import './MarketerProfilePage.css';

const getOfferImage = (offer) => {
  if (offer.coverImage) return offer.coverImage;
  if (Array.isArray(offer.images) && offer.images.length > 0) return offer.images[0];
  return null;
};

const getStatusLabel = (status) => {
  if (status === 'available') return 'متاح';
  if (status === 'reserved') return 'محجوز';
  if (status === 'sold') return 'مباع';
  if (status === 'rented') return 'مؤجر';
  return status || '-';
};

const PROPERTY_TYPE_LABELS = {
  villa: 'فيلا', apartment: 'شقة', land: 'أرض',
  building: 'عمارة', office: 'مكتب', warehouse: 'مستودع', other: 'أخرى'
};

const REQUEST_TYPE_LABELS = { sale: 'شراء', rent: 'إيجار' };

const SORT_OPTIONS = [
  { value: 'newest', label: 'الأحدث أولاً' },
  { value: 'oldest', label: 'الأقدم أولاً' },
  { value: 'priceHigh', label: 'الأعلى سعراً' },
  { value: 'priceLow', label: 'الأقل سعراً' },
];

const MarketerProfilePage = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  const { marketerId } = useParams();
  const location = useLocation();
  const { user: currentUser } = useAuth();

  const initialMarketer = location.state?.marketer || null;

  // Tabs
  const [activeTab, setActiveTab] = useState('offers');

  // Offers state
  const [loadingOffers, setLoadingOffers] = useState(true);
  const [offers, setOffers] = useState([]);
  const [offerPage, setOfferPage] = useState(1);
  const [offerPagination, setOfferPagination] = useState({ current: 1, total: 1, count: 0 });
  const [offerSort, setOfferSort] = useState('newest');
  const [showOfferSortMenu, setShowOfferSortMenu] = useState(false);

  // Requests state
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [requests, setRequests] = useState([]);
  const [requestPage, setRequestPage] = useState(1);
  const [requestPagination, setRequestPagination] = useState({ current: 1, total: 1, count: 0 });
  const [requestSort, setRequestSort] = useState('newest');
  const [showRequestSortMenu, setShowRequestSortMenu] = useState(false);

  // Marketer & follow state
  const [marketer, setMarketer] = useState(initialMarketer);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);

  const [selectedOffer, setSelectedOffer] = useState(null);

  // Sort offers client-side
  const sortedOffers = useMemo(() => {
    const arr = [...offers];
    if (offerSort === 'oldest') return arr.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    if (offerSort === 'priceHigh') return arr.sort((a, b) => (b.price || 0) - (a.price || 0));
    if (offerSort === 'priceLow') return arr.sort((a, b) => (a.price || 0) - (b.price || 0));
    return arr.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [offers, offerSort]);

  const sortedRequests = useMemo(() => {
    const arr = [...requests];
    if (requestSort === 'oldest') return arr.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    if (requestSort === 'priceHigh') return arr.sort((a, b) => (b.budget || b.budgetMax || 0) - (a.budget || a.budgetMax || 0));
    if (requestSort === 'priceLow') return arr.sort((a, b) => (a.budget || a.budgetMax || 0) - (b.budget || b.budgetMax || 0));
    return arr.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [requests, requestSort]);

  const fetchMarketerOffers = useCallback(async (page = 1) => {
    if (!marketerId) return;
    setLoadingOffers(true);
    try {
      const response = await networkService.getFeed({ marketerId, page, limit: 20, sortBy: 'createdAt', sortOrder: 'desc' });
      if (response.success) {
        const dataOffers = response.data?.offers || [];
        setOffers(dataOffers);
        const apiPagination = response.data?.pagination || { current: 1, total: 1, count: dataOffers.length };
        setOfferPagination({ current: apiPagination.current || page, total: apiPagination.total || 1, count: apiPagination.count || dataOffers.length });

        if (dataOffers.length > 0) {
          const firstMarketer = dataOffers[0].marketer || dataOffers[0].user;
          if (firstMarketer) {
            setMarketer((prev) => ({
              ...(prev || {}),
              id: firstMarketer._id || firstMarketer.id || marketerId,
              name: firstMarketer.name || prev?.name || 'مسوق',
              avatar: firstMarketer.avatar || prev?.avatar || '',
              phone: firstMarketer.phone || prev?.phone || '',
              city: firstMarketer.city || prev?.city || '',
              followersCount: firstMarketer.followersCount ?? prev?.followersCount ?? 0,
            }));
            setFollowersCount(firstMarketer.followersCount ?? 0);
          }
        }
      } else {
        setOffers([]);
      }
    } catch (_) {
      setOffers([]);
    } finally {
      setLoadingOffers(false);
    }
  }, [marketerId]);

  const fetchMarketerRequests = useCallback(async (page = 1) => {
    if (!marketerId) return;
    setLoadingRequests(true);
    try {
      const response = await networkService.getMarketerRequests(marketerId, { page, limit: 20 });
      if (response.success) {
        const dataRequests = response.data?.requests || [];
        setRequests(dataRequests);
        const apiPagination = response.data?.pagination || { current: 1, total: 1, count: dataRequests.length };
        setRequestPagination({ current: apiPagination.current || page, total: apiPagination.total || 1, count: apiPagination.count || dataRequests.length });

        // Get marketer info from response if available
        if (response.data?.marketer) {
          const m = response.data.marketer;
          setMarketer((prev) => ({
            ...(prev || {}),
            name: m.name || prev?.name || 'مسوق',
            avatar: m.avatar || prev?.avatar || '',
            city: m.city || prev?.city || '',
            followersCount: m.followersCount ?? prev?.followersCount ?? 0,
          }));
          setFollowersCount(m.followersCount ?? 0);
        }
      } else {
        setRequests([]);
      }
    } catch (_) {
      setRequests([]);
    } finally {
      setLoadingRequests(false);
    }
  }, [marketerId]);

  const checkFollowStatus = useCallback(async () => {
    if (!currentUser || !marketerId) return;
    try {
      const response = await networkService.getUserProfile(marketerId);
      if (response.success) {
        setIsFollowing(response.data?.isFollowing || false);
        setFollowersCount(response.data?.user?.followersCount ?? followersCount);
      }
    } catch (_) {}
  }, [currentUser, marketerId, followersCount]);

  useEffect(() => {
    fetchMarketerOffers(offerPage);
  }, [fetchMarketerOffers, offerPage]);

  useEffect(() => {
    if (activeTab === 'requests' && requests.length === 0) {
      fetchMarketerRequests(requestPage);
    }
  }, [activeTab, fetchMarketerRequests, requestPage, requests.length]);

  useEffect(() => {
    checkFollowStatus();
  }, [checkFollowStatus]);

  const handleFollowToggle = async () => {
    if (!currentUser) return;
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await networkService.unfollow(marketerId);
        setIsFollowing(false);
        setFollowersCount((c) => Math.max(0, c - 1));
      } else {
        await networkService.follow(marketerId);
        setIsFollowing(true);
        setFollowersCount((c) => c + 1);
      }
    } catch (_) {}
    setFollowLoading(false);
  };

  const headerName = useMemo(() => marketer?.name || 'بروفايل المسوق', [marketer]);
  const isOwnProfile = currentUser && (currentUser._id === marketerId || currentUser.id === marketerId);

  return (
    <div className="marketer-profile-page">
      {/* Header */}
      <div className="marketer-profile-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          {isRTL ? <ArrowRight size={18} /> : <ArrowLeft size={18} />}
          <span>رجوع</span>
        </button>

        <div className="marketer-identity">
          <div className="marketer-avatar-lg">
            {marketer?.avatar ? (
              <img src={marketer.avatar} alt={headerName} />
            ) : (
              <span>{headerName.charAt(0)}</span>
            )}
          </div>

          <div className="marketer-meta">
            <h2>{headerName}</h2>
            {marketer?.city && <p><MapPin size={14} /> {marketer.city}</p>}
            {marketer?.phone && (
              <a href={`tel:${marketer.phone}`} className="phone-link">
                <Phone size={14} /> {marketer.phone}
              </a>
            )}
            <div className="marketer-stats-row">
              <span className="followers-count">
                <Users size={14} /> {followersCount} متابع
              </span>
            </div>
          </div>

          {currentUser && !isOwnProfile && (
            <button
              className={`follow-btn ${isFollowing ? 'following' : ''}`}
              onClick={handleFollowToggle}
              disabled={followLoading}
            >
              {followLoading ? (
                <Loader size={16} className="spinner" />
              ) : isFollowing ? (
                <><UserCheck size={16} /> متابَع</>
              ) : (
                <><UserPlus size={16} /> متابعة</>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="marketer-tabs">
        <button
          className={`marketer-tab ${activeTab === 'offers' ? 'active' : ''}`}
          onClick={() => setActiveTab('offers')}
        >
          <Building2 size={16} />
          العروض
          <span className="tab-count">{offerPagination.count || offers.length}</span>
        </button>
        <button
          className={`marketer-tab ${activeTab === 'requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          <FileText size={16} />
          الطلبات
          <span className="tab-count">{requestPagination.count || requests.length}</span>
        </button>
      </div>

      {/* Offers Tab */}
      {activeTab === 'offers' && (
        <>
          <div className="marketer-tab-toolbar">
            <div style={{ position: 'relative' }}>
              <button className="btn btn-outline btn-sm" onClick={() => setShowOfferSortMenu((v) => !v)}>
                <ArrowUpDown size={14} />
                {SORT_OPTIONS.find((o) => o.value === offerSort)?.label || 'الأحدث'}
              </button>
              {showOfferSortMenu && (
                <div className="sort-dropdown-menu">
                  {SORT_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      className={`sort-dropdown-item ${offerSort === opt.value ? 'active' : ''}`}
                      onClick={() => { setOfferSort(opt.value); setShowOfferSortMenu(false); }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {loadingOffers ? (
            <div className="loading-container"><Loader className="spinner" size={42} /><p>جاري تحميل العروض...</p></div>
          ) : sortedOffers.length === 0 ? (
            <div className="empty-state"><Home size={54} /><h3>لا توجد عروض متاحة حالياً</h3></div>
          ) : (
            <div className="offers-list">
              {sortedOffers.map((offer) => (
                <div key={offer._id || offer.id} className="offer-list-item" onClick={() => setSelectedOffer(offer)}>
                  <div className="offer-image-small">
                    {getOfferImage(offer) ? (
                      <img src={getOfferImage(offer)} alt={offer.title} />
                    ) : (
                      <div className="no-image-placeholder">لا توجد صور</div>
                    )}
                  </div>
                  <div className="offer-info">
                    <div className="offer-badges-inline">
                      <span className="type-badge" style={getPropertyTypeColor(offer.propertyType || offer.type)}>
                        {(offer.propertyType || offer.type) || 'عقار'}
                      </span>
                      <span className="status-badge" style={getStatusColor(offer.status)}>
                        {getStatusLabel(offer.status)}
                      </span>
                    </div>
                    <h3>{offer.title || 'بدون عنوان'}</h3>
                    <div className="offer-meta">
                      <span><MapPin size={14} /> {offer.city || '-'} - {offer.district || '-'}</span>
                      {offer.area ? <span><Maximize size={14} /> {formatArea(offer.area)}</span> : null}
                      {offer.rooms ? <span><Bed size={14} /> {offer.rooms} غرف</span> : null}
                    </div>
                    <span className="offer-price-inline">{formatPrice(offer.price)}</span>
                  </div>
                  <div className="offer-price-col">
                    <span className="price">{formatPrice(offer.price)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {offerPagination.total > 1 && (
            <GooglePagination
              currentPage={offerPage}
              totalPages={offerPagination.total}
              onPageChange={setOfferPage}
              prevLabel={t('common.previous')}
              nextLabel={t('common.next')}
              infoPrefix={t('common.page')}
              infoMiddle={t('common.of')}
              isRTL={isRTL}
            />
          )}
        </>
      )}

      {/* Requests Tab */}
      {activeTab === 'requests' && (
        <>
          <div className="marketer-tab-toolbar">
            <div style={{ position: 'relative' }}>
              <button className="btn btn-outline btn-sm" onClick={() => setShowRequestSortMenu((v) => !v)}>
                <ArrowUpDown size={14} />
                {SORT_OPTIONS.find((o) => o.value === requestSort)?.label || 'الأحدث'}
              </button>
              {showRequestSortMenu && (
                <div className="sort-dropdown-menu">
                  {SORT_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      className={`sort-dropdown-item ${requestSort === opt.value ? 'active' : ''}`}
                      onClick={() => { setRequestSort(opt.value); setShowRequestSortMenu(false); }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {loadingRequests ? (
            <div className="loading-container"><Loader className="spinner" size={42} /><p>جاري تحميل الطلبات...</p></div>
          ) : sortedRequests.length === 0 ? (
            <div className="empty-state"><FileText size={54} /><h3>لا توجد طلبات مشتركة حالياً</h3></div>
          ) : (
            <div className="requests-list">
              {sortedRequests.map((req) => (
                <div key={req._id} className="request-card-public">
                  <div className="request-card-header">
                    <span className="request-type-badge">
                      {PROPERTY_TYPE_LABELS[req.propertyType] || req.propertyType || 'عقار'}
                    </span>
                    <span className="request-action-badge">
                      {REQUEST_TYPE_LABELS[req.requestType] || req.requestType || ''}
                    </span>
                  </div>
                  <div className="request-card-body">
                    {(req.city || (req.districts && req.districts.length > 0)) && (
                      <p><MapPin size={14} /> {req.city}{req.districts?.length > 0 ? ` - ${req.districts.join('، ')}` : ''}</p>
                    )}
                    {(req.budget || req.budgetMax) && (
                      <p>الميزانية: {formatPrice(req.budget || req.budgetMax)}</p>
                    )}
                    {req.area && <p>المساحة: {formatArea(req.area)}</p>}
                    {req.requirements && <p className="request-requirements">{req.requirements}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {requestPagination.total > 1 && (
            <GooglePagination
              currentPage={requestPage}
              totalPages={requestPagination.total}
              onPageChange={(p) => { setRequestPage(p); fetchMarketerRequests(p); }}
              prevLabel={t('common.previous')}
              nextLabel={t('common.next')}
              infoPrefix={t('common.page')}
              infoMiddle={t('common.of')}
              isRTL={isRTL}
            />
          )}
        </>
      )}

      {/* Offer Detail Modal */}
      {selectedOffer && (
        <div className="modal-overlay" onClick={() => setSelectedOffer(null)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Eye size={22} /> تفاصيل العرض</h2>
              <button className="modal-close" onClick={() => setSelectedOffer(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="view-offer-content">
                <div className="view-offer-image">
                  {getOfferImage(selectedOffer) ? (
                    <img src={getOfferImage(selectedOffer)} alt={selectedOffer.title} />
                  ) : (
                    <div className="no-image-placeholder" style={{ width: '100%', height: '300px' }}>لا توجد صور</div>
                  )}
                </div>
                <div className="view-offer-details">
                  <h3>{selectedOffer.title || 'بدون عنوان'}</h3>
                  <div className="view-location"><MapPin size={16} /> {selectedOffer.city || '-'} - {selectedOffer.district || '-'}</div>
                  <div className="view-price">{formatPrice(selectedOffer.price)}</div>
                  <div className="view-specs-grid">
                    {selectedOffer.area ? <div className="spec-card"><Maximize size={18} /><div className="spec-info"><span className="spec-label">المساحة</span><span className="spec-value">{formatArea(selectedOffer.area)}</span></div></div> : null}
                    {selectedOffer.rooms ? <div className="spec-card"><Bed size={18} /><div className="spec-info"><span className="spec-label">الغرف</span><span className="spec-value">{selectedOffer.rooms}</span></div></div> : null}
                    {selectedOffer.bathrooms ? <div className="spec-card"><Bath size={18} /><div className="spec-info"><span className="spec-label">الحمامات</span><span className="spec-value">{selectedOffer.bathrooms}</span></div></div> : null}
                  </div>
                  {selectedOffer.description ? <div className="view-description"><h4>الوصف</h4><p>{selectedOffer.description}</p></div> : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketerProfilePage;
